import React from "react";
import { ArrowUpRight, BookOpen } from "lucide-react";
import Seo from "../seo";
import { IMAGES } from "../api";
import { Reveal, Chapter, Breadcrumbs } from "../components/motion";

const RESEARCHGATE = "https://www.researchgate.net/profile/Krishnakanth-Avb";

const PUBLICATIONS = [
    { title: "Upper Urinary Tract Carcinoma With Primary Small Cell Neuroendocrine Cancer — Case Report and Review of Literature", journal: "IJU Case Reports", date: "Aug 2026", url: "https://www.researchgate.net/publication/411736896" },
    { title: "Predictors of Poor Kidney Function in Patients with Emphysematous Pyelonephritis: A Retrospective Observational Study", journal: "Therapeutic Advances in Urology", date: "Mar 2026", url: "https://www.researchgate.net/publication/402786796" },
    { title: "Rupture of Mycotic Aneurysm Complicating Deceased Donor Renal Transplantation: A Dual Case Report", journal: "BMC Urology", date: "Mar 2026", url: "https://www.researchgate.net/publication/401678930" },
    { title: "Unravelling the Added Value of Urinary Stone Cultures Towards Infectious Complications Following Treatment of Renal Stones", journal: "Antibiotics", date: "Jan 2026", url: "https://www.researchgate.net/publication/399462577" },
    { title: "Absolute Eosinophil Count and Albumin–Globulin Ratio as Predictors of Delayed Graft Function in Deceased Donor Kidney Transplant: A Retrospective Analysis", journal: "Kidney and Dialysis", date: "Nov 2025", url: "https://www.researchgate.net/publication/397747049" },
    { title: "Nephrolithometry Scoring Systems in Predicting Acute Kidney Injury Following Percutaneous Nephrolithotomy — A Prospective Observational Study", journal: "Urologia Journal", date: "Aug 2025", url: "https://www.researchgate.net/publication/394525406" },
    { title: "Lower Urinary Tract Dysfunction in Childhood Cancer Survivors — A Prospective Study in a Tertiary Care Hospital", journal: "International Urology and Nephrology", date: "Aug 2025", url: "https://www.researchgate.net/publication/394679006" },
    { title: "Leukaemia-Associated Priapism in Children (LAPC): Reviewing Clinical Outcomes and Management Strategies", journal: "ecancermedicalscience", date: "Feb 2025", url: "https://www.researchgate.net/publication/389413837" },
    { title: "Neutrophil–Lymphocyte Ratio, Platelet–Lymphocyte Ratio, Systemic Inflammatory Response Index, and Systemic Immune-Inflammation Index as Predictors of Metastasis in Renal Cell Carcinoma: A Retrospective Study", journal: "International Journal of Cancer Management", date: "Dec 2024", url: "https://www.researchgate.net/publication/388167772" },
    { title: "Comparative Feasibility Analysis of Retrograde Double J Stenting, Antegrade Double J Stenting, and Percutaneous Nephrostomy Tube Placement for Ureteric Obstruction Secondary to Pelvic Malignancies", journal: "International Journal of Cancer Management", date: "Sep 2024", url: "https://www.researchgate.net/publication/384110341" },
    { title: "Severe Bleeding in Patients Following “Tubeless” Percutaneous Nephrolithotomy: Predictors of Angioembolization", journal: "Urologia Journal", date: "Sep 2024", url: "https://www.researchgate.net/publication/384472708" },
    { title: "Predictors of Outcomes in Conservative Management of High-Grade Renal Trauma", journal: "African Journal of Urology", date: "Sep 2024", url: "https://www.researchgate.net/publication/386164837" },
    { title: "Transurethral Removal of Vesical Fungal Bezoar Using Ho:YAG LASER — A Case Report", journal: "Annals of African Surgery", date: "Jul 2024", url: "https://www.researchgate.net/publication/382766725" },
    { title: "Creeping Suprapubic Catheter Site Growth — A Unique Presentation in a Case of Carcinoma Prostate", journal: "International Journal of Research in Medical Sciences", date: "Jun 2024", url: "https://www.researchgate.net/publication/381831398" },
    { title: "The Uniquities of Rectal Foreign Bodies", journal: "Case report", date: "Jul 2021", url: "https://www.researchgate.net/publication/379597091" },
    { title: "Complications of Sigmoid Diverticulitis — A Case Series", journal: "Case series", date: "Mar 2021", url: "https://www.researchgate.net/publication/379597514" },
    { title: "A Case of Giant Adrenal Adenoma", journal: "Poster", date: "Nov 2020", url: "https://www.researchgate.net/publication/379597522" },
];

const INTERESTS = ["Endourology", "Urolithiasis", "Uro-Oncology", "Laparoscopic Urology", "Renal Transplantation", "Minimally Invasive Surgery"];

const MEMBERSHIPS = [
    "Urological Society of India (USI)",
    "South Zone Urological Society of India (SZUSI)",
    "Karnataka Urological Association (KUA)",
    "Association of Surgeons of India (ASI)",
    "Indian Association of Gastrointestinal Endosurgeons (IAGES)",
];

const PRESENTATIONS = [
    { type: "Paper (podium)", title: "Correlation Between Stone Culture and Infective Complications of Percutaneous Nephrolithotomy", event: "USICON 2025" },
    { type: "Paper (podium)", title: "Comparative Feasibility Analysis of Retrograde Double J Stenting, Antegrade Double J Stenting, and Percutaneous Nephrostomy Tube Placement for Ureteric Obstruction Secondary to Pelvic Malignancies", event: "KUACON 2024" },
    { type: "Poster", title: "Transurethral Removal of Vesical Fungal Bezoar Using Ho:YAG LASER", event: "KUACON 2024" },
    { type: "Paper (podium)", title: "Absolute Eosinophil Count and Albumin Globulin Ratio as Predictors of Delayed Graft Function in Cadaveric Renal Transplants", event: "SZUSICON 2024" },
    { type: "Poster", title: "Creeping Suprapubic Catheter Site Growth", event: "SZUSICON 2024" },
    { type: "Best Paper", title: "Prevalence of Helicobacter pylori in Carcinoma Stomach", event: "Indo-UK SURGICON 2020" },
    { type: "Poster", title: "A Case of Huge Right Adrenal Adenoma", event: "ASICON 2020" },
    { type: "Best Poster", title: "Complications of Sigmoid Diverticulitis", event: "ASICON 2020" },
];

const CME = [
    "Andrology Tutelage — Rupin Shah, KS Hegde Medical Academy, Mangalore",
    "Manipal Research Colloquium 2024",
    "Introduction to Uro-Oncology 2024 — Amrita Institute of Medical Sciences, Kochi",
    "Genito Urinary Imaging Directed Education (GUIDE 3.0) — KS Hegde Medical Academy, Mangalore",
    "UROMET 2024 by USI — Gurgaon",
    "KUACON 2024 — Wayanad",
    "SZUSICON 2024 — Madurai",
    "USICON 2025 — Chennai",
    "COMPLICON 2025 — Bangalore",
];

export default function Research() {
    return (
        <div className="mx-auto max-w-5xl px-5 py-14 lg:px-10 lg:py-20">
            <Seo title="Research & Publications | Dr. Krishnakanth AVB"
                description="Peer-reviewed publications and research of Dr. Krishnakanth AVB in urology — endourology, urolithiasis, uro-oncology and renal transplantation." />
            <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Research" }]} />
            <Chapter index={1} label="Research & Publications" className="mt-10" />
            <Reveal>
                <h1 className="mt-8 max-w-2xl font-display text-4xl font-medium leading-tight text-navy sm:text-5xl">
                    Research & Publications
                </h1>
                <p className="mt-6 max-w-2xl text-base leading-relaxed text-steel md:text-lg">
                    Peer-reviewed work in urology, published in international journals. Each entry links to the full
                    record on ResearchGate.
                </p>
                <a href={RESEARCHGATE} target="_blank" rel="noopener noreferrer" data-testid="researchgate-profile-btn"
                    className="group mt-7 inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-accent hover:shadow-lg">
                    <BookOpen className="h-4 w-4" aria-hidden="true" />
                    View full ResearchGate profile
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
            </Reveal>

            <Reveal delay={0.1} className="mt-14">
                <h2 className="flex items-baseline gap-4 font-display text-2xl font-medium text-navy">
                    <span className="font-mono-label text-accent text-xs">01</span>
                    Research Interests
                </h2>
                <div className="mt-6 flex flex-wrap gap-2" data-testid="research-interests">
                    {INTERESTS.map((i) => (
                        <span key={i} className="rounded-full border border-border bg-mist px-4 py-2 text-xs font-semibold text-navy">
                            {i}
                        </span>
                    ))}
                </div>
            </Reveal>

            <Reveal delay={0.15} className="mt-14">
                <h2 className="flex items-baseline gap-4 font-display text-2xl font-medium text-navy">
                    <span className="font-mono-label text-accent text-xs">02</span>
                    Publications
                </h2>
                <ol className="mt-6 divide-y divide-border border-y border-border" data-testid="publications-list">
                    {PUBLICATIONS.map((p, i) => (
                        <li key={p.url}>
                            <a href={p.url} target="_blank" rel="noopener noreferrer" data-testid={`publication-${i}`}
                                className="group flex items-start justify-between gap-6 py-5">
                                <div className="flex gap-5">
                                    <span className="pt-1 font-mono-label text-[0.62rem] text-slate-400">{String(i + 1).padStart(2, "0")}</span>
                                    <div>
                                        <p className="font-medium leading-snug text-navy transition-colors duration-200 group-hover:text-accent">
                                            {p.title}
                                        </p>
                                        <p className="mt-1.5 text-xs text-steel">
                                            <span className="font-semibold text-navy/70">{p.journal}</span> · {p.date}
                                        </p>
                                    </div>
                                </div>
                                <ArrowUpRight className="mt-1 h-4 w-4 shrink-0 text-slate-300 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-accent" />
                            </a>
                        </li>
                    ))}
                </ol>
            </Reveal>

            <Reveal delay={0.18} className="mt-14">
                <h2 className="flex items-baseline gap-4 font-display text-2xl font-medium text-navy">
                    <span className="font-mono-label text-accent text-xs">03</span>
                    Professional Memberships
                </h2>
                <ul className="mt-6 grid gap-2 sm:grid-cols-2" data-testid="memberships-list">
                    {MEMBERSHIPS.map((m) => (
                        <li key={m} className="rounded-lg border border-border bg-mist px-5 py-3.5 text-sm font-medium text-navy">
                            Member — {m}
                        </li>
                    ))}
                </ul>
            </Reveal>

            <Reveal delay={0.2} className="mt-14">
                <h2 className="flex items-baseline gap-4 font-display text-2xl font-medium text-navy">
                    <span className="font-mono-label text-accent text-xs">04</span>
                    Conference Presentations
                </h2>
                <ol className="mt-6 divide-y divide-border border-y border-border" data-testid="presentations-list">
                    {PRESENTATIONS.map((p, i) => (
                        <li key={i} className="flex items-start gap-5 py-5">
                            <span className="pt-1 font-mono-label text-[0.62rem] text-slate-400">{String(i + 1).padStart(2, "0")}</span>
                            <div>
                                <p className="font-medium leading-snug text-navy">{p.title}</p>
                                <p className="mt-1.5 text-xs text-steel">
                                    <span className={`font-semibold ${p.type.startsWith("Best") ? "text-accent" : "text-navy/70"}`}>{p.type}</span> · {p.event}
                                </p>
                            </div>
                        </li>
                    ))}
                </ol>
            </Reveal>

            <Reveal delay={0.22} className="mt-14">
                <h2 className="flex items-baseline gap-4 font-display text-2xl font-medium text-navy">
                    <span className="font-mono-label text-accent text-xs">05</span>
                    Continued Medical Education, Conferences & Workshops
                </h2>
                <ul className="mt-6 grid gap-2 sm:grid-cols-2" data-testid="cme-list">
                    {CME.map((c) => (
                        <li key={c} className="rounded-lg border border-border bg-mist px-5 py-3.5 text-sm text-navy">
                            {c}
                        </li>
                    ))}
                </ul>
            </Reveal>

            <Reveal className="mt-12 overflow-hidden rounded-2xl">
                <img src={IMAGES.education} alt="Laboratory microscope" className="h-48 w-full object-cover" data-testid="research-image" />
            </Reveal>
        </div>
    );
}
